import type { StreamChunk } from '../providers/types';
import type { ChatMessage } from '../types';
import { BLOCKED_LINK_DOMAINS } from './blockedLinkDomains';

const blockedDomains = new Set(BLOCKED_LINK_DOMAINS);
const pairs: Readonly<Record<string, string>> = { '[': ']', '(': ')', '<': '>', '\u3010': '\u3011', '\uff3b': '\uff3d' };
const squareOpeners = new Set(['[', '\u3010', '\uff3b']);
const boundary = /[\s\u3001\u3002\uff0c\uff01\uff1f\uff1b\uff1a]/u;
interface Range { start: number; end: number; }
interface Group extends Range { opener: string; closed: boolean; }

function containsBlockedDomain(token: string): boolean {
  // ICU-backed normalize can fail in embedded browsers/runtimes. Keep URL
  // detection available with the ASCII-compatible forms used by hostnames.
  let normalized: string;
  try {
    normalized = token.normalize('NFKC');
  } catch {
    normalized = Array.from(token, char => {
      const codePoint = char.codePointAt(0)!;
      if (codePoint >= 0xff01 && codePoint <= 0xff5e) return String.fromCodePoint(codePoint - 0xfee0);
      if (codePoint === 0x3000) return ' ';
      return char;
    }).join('');
  }
  normalized = normalized.replace(/[\u200b-\u200f\u2060\ufeff]/gu, '');
  normalized = normalized.replace(/&#(?:0*46|x0*2e);|&period;/gi, '.');
  // Decode only for detection. Never navigate, expand links, or rewrite normal output.
  for (let pass = 0; pass < 2; pass++) {
    normalized = normalized.replace(/%([0-9a-f]{2})/gi, (_match, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));
  }
  const hosts = normalized.toLowerCase().match(/(?:[a-z0-9-]+\.)+[a-z0-9-]+/g) ?? [];
  return hosts.some(host => {
    const labels = host.split('.');
    return labels.some((_, index) => blockedDomains.has(labels.slice(index).join('.')));
  });
}

function bracketGroups(text: string): Group[] {
  const stack: { start: number; opener: string }[] = [];
  const groups: Group[] = [];
  for (let index = 0; index < text.length; index++) {
    const char = text[index]!;
    if (pairs[char]) stack.push({ start: index, opener: char });
    else if (stack.length && pairs[stack[stack.length - 1]!.opener] === char) {
      groups.push({ ...stack.pop()!, end: index + 1, closed: true });
    }
  }
  for (const group of stack) groups.push({ ...group, end: text.length, closed: false });
  return groups;
}

/** Remove a blocked URL and its enclosing promotion/Markdown, without deleting normal brackets. */
export function filterGeneratedLinks(text: string): string {
  const ranges: Range[] = [];
  const groups = bracketGroups(text);
  // Keep Chinese prose outside a bare URL; URLs inside a bracket remove the complete group.
  const tokens = /[^\s<>[\]()\u3010\u3011\uff3b\uff3d\u3001\u3002\uff0c\uff01\uff1f\uff1b\uff1a\u3400-\u9fff"'`]+/gu;
  for (const match of text.matchAll(tokens)) {
    if (!containsBlockedDomain(match[0])) continue;
    let start = match.index;
    let end = start + match[0].length;
    const enclosing = groups.filter(group => group.start <= start && group.end >= end);
    // Remove the outer square wrapper too, so nested advertisements never leave an empty [].
    const square = enclosing.filter(group => squareOpeners.has(group.opener)).sort((a, b) => a.start - b.start)[0];
    const group = square ?? enclosing.sort((a, b) => b.start - a.start)[0];
    if (group) { start = group.start; end = group.end; }
    // [label](blocked-url), ![label](blocked-url), including a nested URL parenthesis.
    let previous = groups.find(item => squareOpeners.has(item.opener) && item.closed && item.end === start);
    while (previous) {
      start = previous.start;
      previous = groups.find(item => squareOpeners.has(item.opener) && item.closed && item.end === start);
    }
    if (text[start - 1] === '!' && text[start] === '[') start--;
    // A blocked label with a separate Markdown destination must leave neither part behind.
    const destination = groups.find(item => item.opener === '(' && item.start === end);
    if (destination) end = destination.end;
    ranges.push({ start, end });
  }
  if (!ranges.length) return text;
  ranges.sort((a, b) => a.start - b.start);
  let cursor = 0;
  let result = '';
  for (const range of ranges) {
    if (range.start > cursor) result += text.slice(cursor, range.start);
    cursor = Math.max(cursor, range.end);
  }
  return result + text.slice(cursor);
}

/** Old/imported assistant messages use the same policy before display, TTS, and context assembly. */
export function filterAssistantMessage(message: ChatMessage): ChatMessage {
  if (message.role !== 'assistant') return message;
  return {
    ...message,
    content: filterGeneratedLinks(message.content),
    ...(message.reasoning === undefined ? {} : { reasoning: filterGeneratedLinks(message.reasoning) }),
  };
}

class LinkOutputBuffer {
  private pending = '';
  private stack: string[] = [];
  private scanned = 0;

  push(delta: string): string {
    this.pending += delta;
    let safeEnd = 0;
    for (; this.scanned < this.pending.length; this.scanned++) {
      const char = this.pending[this.scanned]!;
      if (pairs[char]) this.stack.push(pairs[char]);
      else if (this.stack[this.stack.length - 1] === char) this.stack.pop();
      if (!this.stack.length && (boundary.test(char) || /[\u3400-\u9fff]/u.test(char))) safeEnd = this.scanned + 1;
    }
    const ready = this.pending.slice(0, safeEnd);
    this.pending = this.pending.slice(safeEnd);
    this.scanned -= safeEnd;
    return filterGeneratedLinks(ready);
  }

  finish(): string {
    // An unfinished bracket/URL is withheld on stop/error as well as normal completion.
    const result = filterGeneratedLinks(this.pending);
    this.pending = ''; this.stack = []; this.scanned = 0;
    return result;
  }
}

/** Quarantine incomplete URLs/brackets so split SSE chunks cannot flash an advertisement. */
export async function* filterGeneratedChunks(source: AsyncIterable<StreamChunk>): AsyncGenerator<StreamChunk> {
  const text = new LinkOutputBuffer();
  const reasoning = new LinkOutputBuffer();
  const flush = (): StreamChunk[] => {
    const chunks: StreamChunk[] = [];
    const pendingText = text.finish();
    const pendingReasoning = reasoning.finish();
    if (pendingText) chunks.push({ type: 'text', delta: pendingText });
    if (pendingReasoning) chunks.push({ type: 'reasoning', delta: pendingReasoning });
    return chunks;
  };
  try {
    for await (const chunk of source) {
      if (chunk.type === 'text' || chunk.type === 'reasoning') {
        const delta = (chunk.type === 'text' ? text : reasoning).push(chunk.delta ?? '');
        if (delta) yield { ...chunk, delta };
      } else if (chunk.type === 'done' || chunk.type === 'error') {
        yield* flush();
        yield chunk.error ? { ...chunk, error: filterGeneratedLinks(chunk.error) } : chunk;
        return;
      } else yield chunk;
    }
  } catch (error) {
    yield* flush();
    throw error;
  }
  yield* flush();
}
